/**
 * KAYA TECHNOLOGIES
 * - Click on a pill toggles its expanded (description) state.
 * - The wrapper is collapsed to N rows by default; "Ver más" expands.
 */
(function () {
  'use strict';

  if (customElements.get('kaya-tech-grid')) return;

  class KayaTechGrid extends HTMLElement {
    constructor() {
      super();
      this._onPillClick = this._onPillClick.bind(this);
      this._onResize = this._onResize.bind(this);
      this._recompute = this._recompute.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('.kaya-tech') || this.parentElement;
      this.toggleBtn = this.section
        ? this.section.querySelector('[data-tech-toggle]')
        : null;
      this.initialRows = parseInt(this.dataset.initialRows, 10) || 2;

      this.addEventListener('click', this._onPillClick);

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this._toggleCollapsed());
      }

      window.addEventListener('resize', this._onResize);

      // Defer until layout is settled (double rAF avoids initial subpixel issues)
      requestAnimationFrame(() => {
        requestAnimationFrame(this._recompute);
      });

      // Recompute when fonts/images load (icon images change pill widths)
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this._recompute);
      }
      const imgs = this.querySelectorAll('img');
      imgs.forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', this._recompute, { once: true });
          img.addEventListener('error', this._recompute, { once: true });
        }
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onPillClick);
      window.removeEventListener('resize', this._onResize);
    }

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(this._recompute, 120);
    }

    _onPillClick(event) {
      const pill = event.target.closest('[data-tech-pill]');
      if (!pill || !this.contains(pill)) return;

      const hasDesc = pill.querySelector('[data-tech-description]');
      if (!hasDesc) return;

      const isExpanded = pill.getAttribute('aria-expanded') === 'true';

      // Collapse any other expanded pill (single-open behaviour)
      this.querySelectorAll('[data-tech-pill][aria-expanded="true"]').forEach(
        (other) => {
          if (other !== pill) other.setAttribute('aria-expanded', 'false');
        }
      );

      pill.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');

      // Layout may shift (pill width changes), recompute the row cutoff
      requestAnimationFrame(this._recompute);
    }

    _toggleCollapsed() {
      const wasCollapsed = this.dataset.collapsed === 'true';
      const isExpandedNow = wasCollapsed; // flipping: was collapsed -> now expanded
      this.dataset.collapsed = isExpandedNow ? 'false' : 'true';

      if (this.toggleBtn) {
        this.toggleBtn.setAttribute(
          'aria-expanded',
          isExpandedNow ? 'true' : 'false'
        );
        const labelEl = this.toggleBtn.querySelector('.kaya-tech__toggle-text');
        if (labelEl) {
          const more = this.toggleBtn.dataset.moreLabel || 'VER MÁS';
          const less = this.toggleBtn.dataset.lessLabel || 'VER MENOS';
          labelEl.textContent = isExpandedNow ? less : more;
        }
      }

      this._applyMaxHeight();

      // If user collapses while a pill is expanded outside the visible rows,
      // close any expanded pill that is now hidden, to avoid confusion.
      if (this.dataset.collapsed === 'true') {
        this._closeHiddenPills();
      }
    }

    _recompute() {
      this._applyMaxHeight();
      this._updateToggleVisibility();
    }

    _applyMaxHeight() {
      const pills = Array.from(this.querySelectorAll('[data-tech-pill]'));
      if (pills.length === 0) {
        this.style.maxHeight = '';
        return;
      }

      if (this.dataset.collapsed === 'false') {
        this.style.maxHeight = this.scrollHeight + 'px';
        // After transition, allow natural growth
        clearTimeout(this._clearMaxTimer);
        this._clearMaxTimer = setTimeout(() => {
          if (this.dataset.collapsed === 'false') this.style.maxHeight = 'none';
        }, 500);
        return;
      }

      // Deterministic cutoff = N * pillHeight + (N - 1) * gap
      // Using the section-configured CSS variable, not measured offsets, so
      // the result is independent of per-block height overrides or subpixel rounding.
      const cs = window.getComputedStyle(this);
      const pillH =
        parseFloat(cs.getPropertyValue('--ktech-pill-h')) || 120;
      const gap = this._getRowGap();
      const N = Math.max(1, this.initialRows);
      const cutoff = N * pillH + (N - 1) * gap;

      // If everything already fits, do not constrain.
      if (this.scrollHeight <= cutoff + 1) {
        this.style.maxHeight = 'none';
        return;
      }

      this.style.maxHeight = Math.floor(cutoff) + 'px';
    }

    _computeRows(pills) {
      const rows = [];
      pills.forEach((pill) => {
        const top = pill.offsetTop;
        const existing = rows.find((r) => Math.abs(r.top - top) < 2);
        if (existing) {
          existing.pills.push(pill);
        } else {
          rows.push({ top: top, pills: [pill] });
        }
      });
      rows.sort((a, b) => a.top - b.top);
      return rows;
    }

    _getRowGap() {
      const cs = window.getComputedStyle(this);
      const gap = parseFloat(cs.rowGap || cs.gap || '0');
      return isNaN(gap) ? 0 : gap;
    }

    _updateToggleVisibility() {
      if (!this.toggleBtn) return;
      const pills = Array.from(this.querySelectorAll('[data-tech-pill]'));
      const rows = this._computeRows(pills);
      const needsToggle = rows.length > this.initialRows;
      this.toggleBtn.hidden = !needsToggle;
    }

    _closeHiddenPills() {
      const cutoff = parseFloat(this.style.maxHeight);
      if (!cutoff || isNaN(cutoff)) return;
      this.querySelectorAll('[data-tech-pill][aria-expanded="true"]').forEach(
        (pill) => {
          if (pill.offsetTop + pill.offsetHeight > cutoff + 1) {
            pill.setAttribute('aria-expanded', 'false');
          }
        }
      );
    }
  }

  customElements.define('kaya-tech-grid', KayaTechGrid);
})();
